import React from "react";
import { IOnGoingTrip } from "@types";
import { formatDateBr, formatDateBrUtc } from "@utils";
import moment from "moment";
import ChipStatus from "../../ChipStatus";
import { getTripStatus, getRouteStatus } from "./TripStatusUtils";

interface TripDetailsProps {
  detail?: IOnGoingTrip | null;
  statusTripNow?: string;
  statusRouteNow?: string;
  progressNow?: string;
  type?: 'PUBLIC' | 'DEFAULT';
}

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-size-14 text-normal line-height-18 text-color-darkest">{children}</span>
);

const Value: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-size-16 text-normal line-height-20 text-color-darkest">{children}</span>
);

export const TripDetails: React.FC<TripDetailsProps> = ({ 
  detail, 
  statusTripNow, 
  statusRouteNow, 
  progressNow,
  type = 'DEFAULT' 
}) => {
  const routeStatus = getRouteStatus(detail, statusRouteNow);
  const rawTripStatus = statusTripNow || getTripStatus(detail);
  const tripStatus = rawTripStatus === "URGENT_ALERT" ? "STARTED" : rawTripStatus;
  const progress = progressNow || detail?.actualProgress || "0%";
  const etaFormatted = detail?.eta 
    ? formatDateBrUtc(moment(detail.eta, 'DD/MM/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ss')) 
    : '--';

  return (
    <div className="margin-top-15 flex-1-1 bg-white border rounded divider-y-1 divider-color-light divider-style-solid">
      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div>
          <Label>Agendamento</Label>
        </div>
        <div style={{ width: 130 }}>
          <Value>{formatDateBr(detail?.scheduledDateTime)}</Value>
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div>
          <Label>Status da rota</Label>
        </div>
        <div style={{ width: 130 }}>
          <ChipStatus status={routeStatus} />
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div>
          <Label>Status da chegada</Label>
        </div>
        <div style={{ width: 130 }}>
          <ChipStatus status={tripStatus} />
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div>
          <Label>Progresso</Label>
        </div>
        <div className="display-flex align-items-center gap-15">
          <div className="width-200">
            <div className="progress">
              <div
                className="progress-bar progress-bar-primary"
                style={{ width: progress }}
              />
            </div>
          </div>
          <Value>{progress}</Value>
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div className="display-flex align-items-center gap-4">
          <span className="rioglyph rioglyph-time-alt text-color-gray"></span>
          <Label>HEC</Label>
        </div>
        <div>
          <Value>{etaFormatted}</Value>
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div className="display-flex align-items-center gap-4">
          <span className="rioglyph rioglyph-time text-color-gray"></span>
          <Label>Duração</Label>
        </div>
        <div>
          <Value>{detail?.duration}</Value>
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div className="display-flex align-items-center gap-4">
          <span className="rioglyph rioglyph-road text-color-gray" />
          <Label>Distância percorrida</Label>
        </div>
        <div>
          <Value>{detail?.distanceTraveled}</Value>
        </div>
      </div>

      <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
        <div className="display-flex align-items-center gap-4">
          <span className="rioglyph rioglyph-road text-color-gray" />
          <Label>Distância restante atual</Label>
        </div>
        <div>
          <Value>{detail?.remainingDistance}</Value>
        </div>
      </div>

      {type !== "PUBLIC" && (
        <div className="display-flex align-items-center justify-content-between padding-y-10 padding-x-15 border-bottom-width-1">
          <div className="display-flex align-items-center gap-4">
            <span className="rioglyph rioglyph-truck text-color-gray"></span>
            <Label>Veículo</Label>
          </div>
          <div>
            <Value>{detail?.assetName}</Value>
          </div>
        </div>
      )}
    </div>
  );
};